/**
 * Grid Alignment Calculator
 * 
 * Calculates the page position needed to align the page's grid system
 * with Illustrator's document grid system.
 * 
 * Problem: Illustrator's document grid starts at (0,0) and has fixed spacing.
 * Our page grid starts at (leftMargin, topMargin) from the page edges.
 * For objects to snap properly, these grid systems must align.
 */

/**
 * Calculate alignment offset for a single margin dimension
 * @param {number} margin - Margin value in same units as lineHeight
 * @param {number} lineHeight - Base grid size (document grid spacing)
 * @returns {number} Offset needed for alignment
 */
function calculateAlignmentOffset(margin, lineHeight) {
  // Calculate offset needed to align grids
  // Page grid line position = pagePosition + margin
  // Document grid lines are at: 0, lineHeight, 2×lineHeight, ...
  // For alignment: pagePosition + margin = n×lineHeight
  // So: pagePosition = n×lineHeight - margin
  
  // Scale to integers to avoid floating point arithmetic entirely
  const scale = 1000; // Handle up to 3 decimal places
  const marginScaled = Math.round(margin * scale);
  const lineHeightScaled = Math.round(lineHeight * scale);
  
  // Perform modulo operations on integers
  const remainder = marginScaled % lineHeightScaled;
  const offsetScaled = (lineHeightScaled - remainder) % lineHeightScaled;
  
  // Scale back to original units
  return offsetScaled / scale;
}

/**
 * Calculate page position for grid alignment with Illustrator 2-decimal constraint handling
 * @param {Object} config - Grid configuration
 * @param {number} config.leftMargin - Left margin in same units as lineHeight
 * @param {number} config.topMargin - Top margin in same units as lineHeight  
 * @param {number} lineHeight - Base grid size (document grid spacing)
 * @returns {Object} Alignment result with ideal and adjusted values
 */
export function calculatePageAlignment(config, lineHeight) {
  const { leftMargin, topMargin } = config;
  
  // Calculate ideal alignment positions (mathematically perfect)
  const idealX = calculateAlignmentOffset(leftMargin, lineHeight);
  const idealY = calculateAlignmentOffset(topMargin, lineHeight);
  
  // Calculate Illustrator-compatible positions (2-decimal constraint, round down)
  const adjustedPositionX = Math.floor(idealX * 100) / 100;
  const adjustedPositionY = Math.floor(idealY * 100) / 100;
  
  // Calculate compensation errors and adjusted margins
  const errorX = idealX - adjustedPositionX;
  const errorY = idealY - adjustedPositionY;
  
  return {
    // Mathematically correct values (backward compatibility)
    x: idealX,
    y: idealY,
    
    // Illustrator-compatible values
    adjustedPosition: {
      x: adjustedPositionX,
      y: adjustedPositionY
    },
    adjustedMargins: {
      left: leftMargin + errorX,
      top: topMargin + errorY
    },
    
    // Metadata about adjustments
    hasAdjustments: Math.abs(errorX) > 0.001 || Math.abs(errorY) > 0.001,
    adjustmentErrors: {
      x: errorX,
      y: errorY
    }
  };
}

/**
 * Verify that the calculated position achieves grid alignment
 * @param {Object} alignmentResult - Result from calculatePageAlignment
 * @param {number} lineHeight - Base grid size
 * @param {Object} originalConfig - Original configuration for ideal verification (optional)
 * @returns {Object} Verification result with ideal and practical alignment status
 */
export function verifyAlignment(alignmentResult, lineHeight, originalConfig = null) {
  // Verify ideal alignment (backward compatibility)
  let idealAlignment = true;
  if (originalConfig) {
    const idealGridLineX = alignmentResult.x + originalConfig.leftMargin;
    const idealGridLineY = alignmentResult.y + originalConfig.topMargin;
    
    const alignedX = Math.abs(idealGridLineX % lineHeight) < 0.001;
    const alignedY = Math.abs(idealGridLineY % lineHeight) < 0.001;
    
    idealAlignment = alignedX && alignedY;
  }
  
  // Verify practical alignment using adjusted values
  const practicalGridLineX = alignmentResult.adjustedPosition.x + alignmentResult.adjustedMargins.left;
  const practicalGridLineY = alignmentResult.adjustedPosition.y + alignmentResult.adjustedMargins.top;
  
  const practicalAlignedX = Math.abs(practicalGridLineX % lineHeight) < 0.001;
  const practicalAlignedY = Math.abs(practicalGridLineY % lineHeight) < 0.001;
  
  const practicalAlignment = practicalAlignedX && practicalAlignedY;
  
  return {
    idealAlignment,
    practicalAlignment,
    bothAligned: idealAlignment && practicalAlignment
  };
}

/**
 * Get human-readable alignment instructions using Illustrator-compatible positioning
 * @param {Object} alignmentResult - Result from calculatePageAlignment
 * @param {string} unit - Unit string ('pt', 'px')
 * @returns {string} Instructions for positioning the page
 */
export function getAlignmentInstructions(alignmentResult, unit) {
  const { adjustedPosition, hasAdjustments, x, y } = alignmentResult;
  
  if (adjustedPosition.x === 0 && adjustedPosition.y === 0) {
    return `Place page at document origin (0, 0). The grid is already aligned.`;
  }
  
  // Format numbers - use 2 decimal places for Illustrator precision
  const xFormatted = adjustedPosition.x.toFixed(2).replace(/\.?0+$/, '');
  const yFormatted = adjustedPosition.y.toFixed(2).replace(/\.?0+$/, '');
  
  let instruction = `Place page at (${xFormatted}${unit}, ${yFormatted}${unit}) to align with document grid.`;
  
  // Add adjustment warning if position was rounded
  if (hasAdjustments) {
    const idealXFormatted = x.toFixed(3).replace(/\.?0+$/, '');
    const idealYFormatted = y.toFixed(3).replace(/\.?0+$/, '');
    instruction += ` (Position rounded from ${idealXFormatted}${unit}, ${idealYFormatted}${unit} for Illustrator compatibility)`;
  }
  
  return instruction;
}